import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { apiClient } from '@/lib/apiClient';
import { getCookie, setCookie, deleteCookie } from '@/lib/cookies';
import { getSharedCookieDomain } from '@/lib/subdomain';
import { getPersianErrorMessage } from '@/lib/persianUi';

export const AuthContext = createContext(null);

const ACTIVE_ROLE_KEY = 'active_role';
const PRE_AUTH_TOKEN_KEY = 'pre_auth_token';
const PENDING_ROLES_KEY = 'pending_roles';

const getRoleType = (role) => role?.type ?? role?.role ?? null;

const normalizeRoles = (nextUser) => nextUser?.roles ?? [];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState(() => {
    try {
      const saved = sessionStorage.getItem(PENDING_ROLES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [activeRole, setActiveRoleState] = useState(
      () => getCookie(ACTIVE_ROLE_KEY)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inflightAuthCheck = useRef(null);

  const checkAuth = useCallback(async () => {
    if (inflightAuthCheck.current) {
      return inflightAuthCheck.current;
    }
    const promise = (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error: apiError } = await apiClient.GET('/api/v1/auth/me');
        if (apiError || !data) {
          // اگر کاربر در مرحله انتخاب نقش است، نباید اطلاعاتش پاک شود
          if (sessionStorage.getItem(PRE_AUTH_TOKEN_KEY)) {
            return null;
          }
          setUser(null);
          setRoles([]);
          setActiveRoleState(null);
          deleteCookie(ACTIVE_ROLE_KEY, { domain: getSharedCookieDomain() });
          return null;
        }
        const nextRoles = normalizeRoles(data);
        const storedRole = getCookie(ACTIVE_ROLE_KEY);
        const nextActiveRole = nextRoles.some((role) => getRoleType(role) === storedRole)
            ? storedRole
            : nextRoles.length === 1
                ? getRoleType(nextRoles[0])
                : null;

        setUser(data);
        setRoles(nextRoles);
        setActiveRoleState(nextActiveRole);
        if (nextActiveRole) {
          setCookie(ACTIVE_ROLE_KEY, nextActiveRole, { domain: getSharedCookieDomain() });
        } else {
          deleteCookie(ACTIVE_ROLE_KEY, { domain: getSharedCookieDomain() });
        }
        return data;
      } catch (err) {
        console.error('Auth check error:', err);
        return null;
      } finally {
        setLoading(false);
        inflightAuthCheck.current = null;
      }
    })();
    inflightAuthCheck.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Login via FastAPI OAuth2PasswordRequestForm.
   */
  const login = async (
    mobilePhone,
    password,
    captchaToken = null
  ) => {
    try {
      setError(null);
      setLoading(true);

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };
      if (captchaToken) {
        headers['X-Turnstile-Token'] = captchaToken;
      }

      const {
        data: loginData,
        error: apiError,
        response,
      } = await apiClient.POST(
        '/api/v1/auth/login',
        {
          body: {
            username: mobilePhone,
            password,
            scope: '',
          },
          bodySerializer: (body) =>
            new URLSearchParams(body).toString(),
          headers,
        }
      );

      // 428 Precondition Required: backend requires captcha before continuing
      if (response && response.status === 428) {
        const err = new Error('captcha_required');
        err.code = 'captcha_required';
        setError('captcha_required');
        throw err;
      }

      if (apiError) {
        throw new Error(
          getPersianErrorMessage(apiError, 'ورود به حساب کاربری ناموفق بود.'),
        );
      }

      if (!loginData) throw new Error('ورود به حساب کاربری ناموفق بود');

      if (loginData.requires_role_selection) {
        // === [ADDED] انتخاب خودکار نقش برای کاربرانی که فقط یک نقش دارند ===
        const availableRoles = loginData.available_roles ?? [];
        if (availableRoles.length === 1) {
          const onlyRole = getRoleType(availableRoles[0]);
          const preAuthToken = loginData.pre_auth_token;
          if (preAuthToken) {
            try {
              const { data: selectData, error: selectError } = await apiClient.POST(
                  '/api/v1/auth/select-role',
                  {
                    body: { role: onlyRole },
                    headers: { 'X-Pre-Auth-Token': preAuthToken },
                  }
              );
              if (!selectError && selectData) {
                const freshUser = await checkAuth();
                return {
                  requiresRoleSelection: false,
                  user: freshUser,
                  role: onlyRole,
                  redirectTo: selectData.redirect_to,
                };
              }
            } catch (e) {
              console.error("Auto-select single role failed:", e);
            }
          }
        }
        // === [END ADDED] ===

        console.log("پاسخ لاگین بک‌اند (جهت بررسی توکن):", loginData);

        setUser(null);
        setRoles(availableRoles);
        setActiveRoleState(null);
        deleteCookie(ACTIVE_ROLE_KEY, { domain: getSharedCookieDomain() });
        sessionStorage.setItem(PENDING_ROLES_KEY, JSON.stringify(availableRoles));

        if (loginData.pre_auth_token) {
          sessionStorage.setItem(PRE_AUTH_TOKEN_KEY, loginData.pre_auth_token);
        } else {
          console.error("🚨 خطا: بک‌اند فیلد pre_auth_token را برنگرداند!");
        }
        return { requiresRoleSelection: true, roles: availableRoles };
      }

      const freshUser = await checkAuth();
      if (!freshUser) throw new Error('ورود انجام شد اما اطلاعات کاربر دریافت نشد');

      return {
        requiresRoleSelection: false,
        user: freshUser,
        role: getRoleType(freshUser?.roles?.[0]),
        redirectTo: loginData.redirect_to,
      };
    } catch (err) {
      const message = getPersianErrorMessage(
        err,
        'ورود به حساب کاربری ناموفق بود.',
      );
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectRole = async (nextRole) => {
    const preAuthToken = sessionStorage.getItem(PRE_AUTH_TOKEN_KEY);
    console.log("توکن موقت پیدا شده در مرورگر:", preAuthToken);

    if (!preAuthToken) {
      // 👈 اینجا به جای ارور دادن، کاربر را به لاگین می‌فرستیم تا نشست جدید بسازد
      await logout();
      window.location.assign('/login');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: apiError } = await apiClient.POST(
          '/api/v1/auth/select-role',
          {
            body: { role: nextRole },
            headers: { 'X-Pre-Auth-Token': preAuthToken },
          }
      );

      if (apiError || !data) {
        throw new Error(
          getPersianErrorMessage(apiError, 'انتخاب نقش ناموفق بود.'),
        );
      }

      sessionStorage.removeItem(PRE_AUTH_TOKEN_KEY);
      sessionStorage.removeItem(PENDING_ROLES_KEY);

      setActiveRoleState(nextRole);
      setCookie(ACTIVE_ROLE_KEY, nextRole, { domain: getSharedCookieDomain() });

      const authenticatedUser = await checkAuth();
      if (!authenticatedUser) {
        throw new Error('ورود انجام شد اما اطلاعات کاربر دریافت نشد');
      }

      return { redirectTo: data.redirect_to };
    } catch (err) {
      setError(err?.message || 'انتخاب نقش ناموفق بود');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiClient.POST('/api/v1/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setRoles([]);
      setActiveRoleState(null);
      deleteCookie(ACTIVE_ROLE_KEY, { domain: getSharedCookieDomain() });
      sessionStorage.removeItem(PRE_AUTH_TOKEN_KEY);
      sessionStorage.removeItem(PENDING_ROLES_KEY);
      setLoading(false);
    }
  };

  const vendorRole = roles.find((role) => getRoleType(role) === 'vendor');
  const referralEnabled =
    activeRole === 'vendor' && Boolean(vendorRole?.referral_enabled);
  const value = {
    user,
    roles,
    activeRole,
    loading,
    error,
    login,
    selectRole,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    role: activeRole,

    isSuperAdmin:
      activeRole === 'super_admin',

    isIrancharsouqAdmin:
      activeRole === 'irancharsouq_admin',

    isInspector:
      activeRole === 'inspector',

    referralEnabled,

  };

  return (
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>
  );
};